import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { dealsService } from '../../services/supabaseService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import ProfileHeader from './components/ProfileHeader';
import RatingsBreakdown from './components/RatingsBreakdown';
import ReviewsList from './components/ReviewsList';
import ActiveTrips from './components/ActiveTrips';
import AcceptedOffers from './components/AcceptedOffers';
import TrustIndicators from './components/TrustIndicators';

const TravelerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [profile, setProfile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      title: 'Traveler Profile',
      loading: 'Loading profile...',
      notFound: 'Profile not found',
      backToDashboard: 'Back to Dashboard'
    },
    ko: {
      title: '여행자 프로필',
      loading: '프로필 로딩 중...',
      notFound: '프로필을 찾을 수 없습니다',
      backToDashboard: '대시보드로 돌아가기'
    }
  };

  const t = translations?.[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (id) {
      loadProfileData();
    }
  }, [id]);

  const loadProfileData = async () => {
    try {
      setLoading(true);

      // Load traveler profile
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (profileError) throw profileError;

      // Load active trips
      const { data: tripsData, error: tripsError } = await supabase
        ?.from('traveler_trips')
        ?.select('*')
        ?.eq('traveler_id', id)
        ?.eq('status', 'active')
        ?.order('departure_date', { ascending: true });

      if (tripsError && tripsError?.code !== 'PGRST116') {
        console.error('Trips error:', tripsError);
      }

      // Load deals (accepted offers)
      const dealsData = await dealsService?.getByTravelerId(id);

      setProfile(profileData);
      setTrips(tripsData || []);
      setDeals(dealsData || []);
    } catch (error) {
      console.error('Load profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t?.loading}</p>
          </div>
        </main>
        <BottomTabNavigation />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-lg text-foreground mb-4">{t?.notFound}</p>
            <button
              onClick={() => navigate('/traveler-dashboard')}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t?.backToDashboard}
            </button>
          </div>
        </main>
        <BottomTabNavigation />
      </div>
    );
  }

  const acceptedOffers = deals?.filter(d => ['accepted', 'in_transit', 'delivered']?.includes(d?.status)) || [];
  const completedDeliveries = deals?.filter(d => d?.status === 'completed') || [];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pb-20 md:pb-6">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          {/* Profile Header */}
          <ProfileHeader profile={profile} currentLanguage={currentLanguage} />

          {/* Trust Indicators */}
          <div className="mt-6">
            <TrustIndicators profile={profile} currentLanguage={currentLanguage} />
          </div>

          {/* Ratings & Reviews Section */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RatingsBreakdown 
              profile={profile} 
              completedCount={completedDeliveries?.length}
              currentLanguage={currentLanguage} 
            />
            <ReviewsList 
              travelerId={id}
              currentLanguage={currentLanguage} 
            />
          </div>

          {/* Active Trips & Accepted Offers */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ActiveTrips trips={trips} currentLanguage={currentLanguage} />
            <AcceptedOffers offers={acceptedOffers} currentLanguage={currentLanguage} />
          </div>
        </div>
      </main>
      <BottomTabNavigation />
    </div>
  );
};

export default TravelerProfile;